<script lang="ts" setup>
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { useUserStore } from '../stores/user/user.store'
import ProfileDetailsForm from '../components/forms/ProfileDetailsForm.vue'
import PreferencesForm from '../components/forms/PreferencesForm.vue'
import NotificationPreferencesCard from '../components/settings/NotificationPreferencesCard.vue'
import PushNotificationsCard from '../components/settings/PushNotificationsCard.vue'
import DeleteAccountCard from '../components/settings/DeleteAccountCard.vue'

const $q = useQuasar()
const userStore = useUserStore()
const { userProfile } = storeToRefs(userStore)

const editingProfileDetails = ref(false)

const sections = [
  { id: 'profile', label: 'Profile' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'delete-account', label: 'Delete account' }
]

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function onSave(message: string) {
  $q.notify({
    color: 'positive',
    textColor: 'white',
    icon: 'mdi-check',
    message: message
  })
}
</script>

<template>
  <q-page padding class="q-pa-md">
    <div v-if="userProfile" class="row no-wrap layout-wide q-mx-auto q-col-gutter-lg">
      <!-- Section list: sticky beside the cards on large screens, chips above them on small ones -->
      <nav class="gt-md col-auto settings-nav" aria-label="Account sections">
        <q-list>
          <q-item
            v-for="section in sections"
            :key="section.id"
            clickable
            :data-testid="`nav-${section.id}`"
            @click="scrollTo(section.id)"
          >
            <q-item-section>{{ section.label }}</q-item-section>
          </q-item>
        </q-list>
      </nav>

      <div class="col settings-column">
        <h1 class="text-h5 q-mt-none q-mb-md">Account</h1>

        <div class="lt-lg q-mb-md settings-chips" role="navigation" aria-label="Account sections">
          <q-chip
            v-for="section in sections"
            :key="section.id"
            clickable
            outline
            color="primary"
            :label="section.label"
            :data-testid="`chip-${section.id}`"
            @click="scrollTo(section.id)"
          />
        </div>

        <div class="column q-gutter-y-md">
          <q-card flat bordered id="profile" class="settings-card" data-testid="profile-card">
            <q-card-section>
              <div class="text-h6">Profile</div>
            </q-card-section>

            <q-card-section v-show="!editingProfileDetails">
              <div class="row items-center no-wrap q-gutter-md">
                <q-avatar size="64px">
                  <img :src="userProfile.picture || undefined" alt="" />
                </q-avatar>
                <div class="col">
                  <div class="text-subtitle1">{{ userProfile.name }}</div>
                  <div class="text-body2 text-grey-8">{{ userProfile.nickname }}</div>
                </div>
              </div>

              <q-list>
                <q-item>
                  <q-item-section avatar>
                    <q-icon color="primary" name="mdi-email" />
                  </q-item-section>
                  <q-item-section class="ellipsis">{{ userProfile.emailAddress }}</q-item-section>
                </q-item>
                <q-item v-show="userProfile.phoneNumber">
                  <q-item-section avatar>
                    <q-icon
                      color="primary"
                      :name="
                        userProfile.isPhoneNumberVerified ? 'mdi-phone-check' : 'mdi-phone-alert'
                      "
                    />
                  </q-item-section>
                  <q-item-section>{{ userProfile.phoneNumber }}</q-item-section>
                </q-item>
                <q-item>
                  <q-item-section avatar>
                    <q-icon color="primary" name="mdi-map-marker" />
                  </q-item-section>
                  <q-item-section>{{ userProfile.countryCode }}</q-item-section>
                </q-item>
              </q-list>

              <q-btn
                outline
                color="primary"
                label="Edit profile"
                class="q-mt-sm"
                data-testid="edit-profile-button"
                @click="editingProfileDetails = true"
              />
            </q-card-section>

            <q-card-section v-if="editingProfileDetails">
              <profile-details-form
                @save="
                  () => {
                    editingProfileDetails = false
                    onSave('Your profile has been updated.')
                  }
                "
                @reset="editingProfileDetails = false"
              />
            </q-card-section>

            <q-separator />
            <q-card-section>
              <div class="text-subtitle1 q-mb-sm">App preferences</div>
              <preferences-form @save="onSave('Your preferences have been saved.')" />
            </q-card-section>
          </q-card>

          <notification-preferences-card />

          <push-notifications-card />

          <delete-account-card />
        </div>
      </div>
    </div>
  </q-page>
</template>

<style scoped>
.settings-column {
  min-width: 0;
  max-width: 720px;
}

.settings-chips {
  display: flex;
  flex-wrap: wrap;
}

.settings-card {
  scroll-margin-top: 72px;
}

.settings-nav {
  position: sticky;
  top: 72px;
  align-self: flex-start;
  min-width: 180px;
}
</style>
